import { useRef, useLayoutEffect } from 'react'
import cn from 'classnames'
import { bool, string, number, func, instanceOf } from 'prop-types'

import CarouselContributor from './carousel-contributor.js'
import CarouselPostContent from './carousel-post-content.js'
import CarouselPostMedia from './carousel-post-media.js'
import Settings from '../../../../../models/settings.js'
import keyPress, { TAB_KEY, shiftAndTabPress } from '../../../../utilities/keyPress.js'
import Post from '../../../../../models/Post.js'
import Locale from '../../../../../models/Locale.js'

const CarouselFeedPost = ({
  leaveCarousel,
  hasLeftCarousel,
  observer,
  slickAutoplay,
  feedKey,
  locale,
  maxPosts,
  openPost,
  post,
  postWidth,
  goToPost,
  settings,
  postIndex,
  toggleAutoPlay,
  toggleHasLeftCarousel,
}) => {
  const { fontFamily, isMinimalistic, postBackgroundColor, postColor } = settings
  const postCounter = `${postIndex + 1}/${maxPosts}`
  const mainRef = useRef()

  useLayoutEffect(() => {
    const { current } = mainRef
    observer.observe(current)
    return () => {
      observer.unobserve(current)
    }
  }, [observer])

  return (
    <div
      ref={mainRef}
      className={cn('bzfy-c-carousel-post', {
        'bzfy-c-carousel-post--minimalistic': isMinimalistic,
      })}
      data-post-key={post.key}
      onBlur={() => {
        if (!slickAutoplay && settings.isAutoTheme) {
          toggleAutoPlay(true)
        }
      }}
      onClick={() => {
        openPost()
      }}
      onFocus={() => {
        if (hasLeftCarousel) {
          toggleHasLeftCarousel()
          if (!settings.isAutoTheme) {
            document.getElementById(`post-0`).focus()
            goToPost(0)
          }
        }
        if (slickAutoplay && settings.isAutoTheme) {
          toggleAutoPlay(false)
        }
        goToPost(postIndex)
      }}
      onKeyDown={(e) => {
        if (postIndex === maxPosts - 1) {
          if (shiftAndTabPress(e)) {
            return
          }
          keyPress(e, () => leaveCarousel(), TAB_KEY)
        }
        keyPress(e, () => openPost())
      }}
      role="link"
      style={{
        backgroundColor: postBackgroundColor,
        color: postColor,
        fontFamily,
      }}
      tabIndex={0}
      title={locale.getTranslation('openTheGate')}
    >
      <CarouselContributor post={post} settings={settings} />
      <CarouselPostMedia
        feedKey={feedKey}
        post={post}
        postCounter={postCounter}
        postWidth={postWidth}
        settings={settings}
      />
      <CarouselPostContent feedKey={feedKey} post={post} settings={settings} />
    </div>
  )
}

CarouselFeedPost.propTypes = {
  feedKey: string.isRequired,
  goToPost: func.isRequired,
  hasLeftCarousel: bool.isRequired,
  leaveCarousel: func.isRequired,
  locale: instanceOf(Locale).isRequired,
  maxPosts: number.isRequired,
  observer: instanceOf(IntersectionObserver).isRequired,
  openPost: func.isRequired,
  post: instanceOf(Post).isRequired,
  postIndex: number.isRequired,
  postWidth: number.isRequired,
  settings: instanceOf(Settings).isRequired,
  slickAutoplay: bool.isRequired,
  toggleAutoPlay: func.isRequired,
  toggleHasLeftCarousel: func.isRequired,
}

export default CarouselFeedPost
